import {
  UsersTableSearchProps,
  UsersTableSortProps
} from './users-table-store.types'
import { PaginationSearchBase, TriggerSearchFunc } from 'mobx-stores/utils'
import {
  // RootStore,
  StoresMainStore,
  UsersMainStore,
  UsersTableEntry
} from 'mobx-stores'
import {
  initialUsersTablePaginationProps,
  initialUsersTableSearchProps,
  usersTableNeedTimeoutProps
} from './users-table-store.utils'
import moment from 'moment'
import {
  getSortingResult,
  SelectOption,
  toLowerCaseTrimmed
} from '@touche-lts/panel-ui/dist'

export class UsersTableStore extends PaginationSearchBase<
  UsersTableSortProps,
  UsersTableSearchProps,
  UsersTableEntry
> {
  private usersMainStore: UsersMainStore
  private storesMainStore: StoresMainStore
  // private rootStore: RootStore

  constructor(rootStore: any) {
    super({
      initialPaginationProps: initialUsersTablePaginationProps,
      initialSearchProps: initialUsersTableSearchProps,
      needTimeoutProps: usersTableNeedTimeoutProps
    })
    this.usersMainStore = rootStore.usersMainStore
    this.storesMainStore = rootStore.storesMainStore
    // this.rootStore = rootStore
  }

  // METHODS
  public onTableMount = async (): Promise<void> => {
    await this.usersMainStore.loadUsers()
    await this.storesMainStore.loadUsersStores()
    this.triggerSearch()
  }

  public triggerSearch: TriggerSearchFunc = (options): void => {
    const { name, roleId, email, storeName, addedOnPeriod } = this.searchProps
    const nameSearch = toLowerCaseTrimmed(name)
    const emailSearch = toLowerCaseTrimmed(email)
    const storeNameSearch = toLowerCaseTrimmed(storeName)

    const results = this.usersTableEntries.filter(tableEntry => {
      const nameFilter = nameSearch
        ? tableEntry.fullName.toLowerCase().includes(nameSearch)
        : true
      const roleFilter = roleId ? tableEntry.role.id === roleId : true
      const emailFilter = emailSearch
        ? tableEntry.emailAddress.toLowerCase().includes(emailSearch)
        : true
      const storeFilter = storeNameSearch
        ? tableEntry.stores.some(({ name }) =>
          name.toLowerCase().includes(storeNameSearch)
        )
        : true
      const addedOnFilter = addedOnPeriod
        ? moment(tableEntry.addedOn).isBetween(
          addedOnPeriod.start,
          addedOnPeriod.end,
          undefined,
          '[]'
        )
        : true

      return (
        nameFilter && roleFilter && emailFilter && storeFilter && addedOnFilter
      )
    })

    options?.resetPageNumber && this.setPaginationProps({ page: 0 })
    const { sortProp, sortDirection, page, size } = this.pagination
    results.sort((b, a) => {
      switch (sortProp) {
        case UsersTableSortProps.NAME: {
          return getSortingResult(a.fullName, b.fullName, sortDirection)
        }
        case UsersTableSortProps.ROLE: {
          return getSortingResult(a.role?.name, b.role?.name, sortDirection)
        }
        case UsersTableSortProps.EMAIL: {
          return getSortingResult(a.emailAddress, b.emailAddress, sortDirection)
        }
        case UsersTableSortProps.STORE: {
          return getSortingResult(
            a.stores[0]?.name,
            b.stores[0]?.name,
            sortDirection
          )
        }
        case UsersTableSortProps.ADDED_ON: {
          return getSortingResult(
            new Date(a.addedOn),
            new Date(b.addedOn),
            sortDirection
          )
        }
        default:
          return 0
      }
    })
    this.setPaginationProps({
      totalPages: Math.ceil(results.length / size),
      totalElements: results.length
    })
    
    const nextPage = page + 1;
    if(results.length >= nextPage * size)
      this.setSearchResults(results.slice(page * size, nextPage *size))
    else
      this.setSearchResults(results.slice(page * size, results.length))
  }

  // COMPUTED
  private get usersTableEntries(): UsersTableEntry[] {
    return this.usersMainStore.usersList.map(user => ({
      id: user.id,
      imageUrl: user.imageUrl,
      fullName: (user.firstName + ' ' + user.lastName).trim(),
      role: user.role,
      emailAddress: user.emailAddress,
      stores: this.storesMainStore.getStoresListByUserEmail(user.emailAddress),
      addedOn: user.created
    }))
  }

  public get userRolesAsSelectOptions(): SelectOption<number>[] {
    return this.usersMainStore.usedUserRolesList.map(role => ({
      label: role.label,
      value: role.id
    }))
  }
}
